// LICENSE_CODE ZON
'use strict'; /*jslint ts:true, react:true*/
import React, {useId, useState} from 'react';
import cn from 'classnames';
import Icon_chevron_down from '../../img/svg/icon/chevron_down.svg';
import styles from './accordion.module.less';

export interface i_accordion_item {
  key?: string;
  title: React.ReactNode;
  content?: React.ReactNode;
  icon?: React.ReactNode;
  marker?: React.ReactNode;
  default_open?: boolean;
  disabled?: boolean;
  attrs?: Record<string, string|number|boolean|undefined>;
}

export interface i_accordion_classes {
  root?: string;
  item?: string;
  item_open?: string;
  trigger?: string;
  icon?: string;
  title?: string;
  marker?: string;
  panel?: string;
  content?: string;
}

// a node, or a render function called with the item it belongs to
export type t_accordion_slot = React.ReactNode
  |((item: i_accordion_item, index: number, is_open: boolean)=>
    React.ReactNode);

export interface i_accordion_props {
  items: i_accordion_item[];
  classes?: i_accordion_classes;
  cls?: string;
  style?: React.CSSProperties;
  icon?: t_accordion_slot;
  marker?: t_accordion_slot;
  duration?: number;
  gap?: number|string;
  multiple?: boolean;
  opened?: number[];
  on_toggle?: (index: number, is_open: boolean)=>void;
}

const slot_node = (slot: t_accordion_slot, item: i_accordion_item,
  index: number, is_open: boolean): React.ReactNode=>
{
  if (typeof slot=='function')
    return slot(item, index, is_open);
  return slot;
};

const motion_style = (duration: number, gap?: number|string,
  style?: React.CSSProperties)=>
{
  const vars: Record<string, string> = {
    '--accordion_duration': `${duration}ms`,
  };
  if (gap!==undefined)
    vars['--accordion_gap'] = typeof gap=='number' ? `${gap}px` : gap;
  return {...style, ...vars};
};

export const Accordion = (props: i_accordion_props)=>{
  const {items, classes={}, cls, style, icon, marker, duration=280, gap,
    multiple=true, opened, on_toggle} = props;
  const uid = useId();
  const [self_opened, set_self_opened] = useState<number[]>(()=>
    items.reduce<number[]>((result, item, index)=>{
      if (item.default_open)
        result.push(index);
      return result;
    }, []));
  const open_list = opened||self_opened;

  const toggle = (index: number)=>{
    const is_open = open_list.includes(index);
    if (!opened)
    {
      set_self_opened(current=>{
        if (current.includes(index))
          return current.filter(item=>item!=index);
        return multiple ? [...current, index] : [index];
      });
    }
    on_toggle?.(index, !is_open);
  };

  return <div className={cn(styles.accordion, classes.root, cls)}
    style={motion_style(duration, gap, style)}>
    {items.map((item, index)=>{
      const is_open = open_list.includes(index);
      const panel_id = `${uid}_${index}`;
      const icon_node = item.icon!==undefined
        ? item.icon : slot_node(icon, item, index, is_open);
      const marker_node = item.marker!==undefined
        ? item.marker
        : marker===undefined
          ? <Icon_chevron_down/>
          : slot_node(marker, item, index, is_open);

      return <article
        key={item.key||index}
        className={cn(styles.item, classes.item,
          is_open&&classes.item_open)}
        data-open={is_open||undefined}
        {...item.attrs}>
        <button
          type="button"
          className={cn(styles.trigger, classes.trigger)}
          disabled={item.disabled}
          aria-expanded={is_open}
          aria-controls={panel_id}
          onClick={()=>toggle(index)}>
          {!!icon_node&&<span className={cn(styles.icon, classes.icon)}
            aria-hidden="true">
            {icon_node}
          </span>}

          <span className={cn(styles.title, classes.title)}>
            {item.title}
          </span>

          {!!marker_node&&<span className={cn(styles.marker, classes.marker)}
            aria-hidden="true">
            {marker_node}
          </span>}
        </button>

        <div
          id={panel_id}
          role="region"
          className={cn(styles.panel, classes.panel)}
          data-open={is_open||undefined}
          aria-hidden={!is_open}>
          <div className={cn(styles.content, classes.content)}>
            {item.content}
          </div>
        </div>
      </article>;
    })}
  </div>;
};

export interface i_accordion_panel_props {
  open: boolean;
  id?: string;
  cls?: string;
  content_cls?: string;
  duration?: number;
  style?: React.CSSProperties;
  children?: React.ReactNode;
}
export const Accordion_panel = (props: i_accordion_panel_props)=>{
  const {open, id, cls, content_cls, duration=280, style, children} = props;
  return <div
    id={id}
    className={cn(styles.panel, cls)}
    style={motion_style(duration, undefined, style)}
    data-open={open||undefined}
    aria-hidden={!open}>
    <div className={cn(styles.content, content_cls)}>
      {children}
    </div>
  </div>;
};
